<template>
    <div class="accessories-selector">

        <!-- Title and Results Count -->
        <div class="accessories-selector__header">
            <div class="accessories-selector__header-right">
                <div class="accessories-selector__results">
                    {{ resultsCountText }}
                </div>
                <button
                    v-if="hasActiveFilters"
                    type="button"
                    @click="resetFilters"
                    class="accessories-selector__reset-button"
                >
                    {{ translations.resetFilters || 'Reset filters' }}
                </button>
            </div>
        </div>

        <!-- Filters Section -->
        <section class="accessories-selector__filters" v-if="filterKeys.length > 0">
            <div class="accessories-selector__filters-container">
                <div
                    v-for="filterKey in filterKeys"
                    :key="filterKey"
                    class="accessories-selector__filter-group"
                >
                    <label class="accessories-selector__label">
                        {{ getColumnLabel(filterKey) }}
                    </label>
                    <v-select
                        v-model="activeFilters[filterKey]"
                        :options="availableFilterOptions[filterKey] || []"
                        :placeholder="translations.choose || 'Select'"
                        :clearable="true"
                        :multiple="true"
                        class="accessories-selector__v-select"
                    />
                </div>
            </div>
        </section>

        <!-- Accessories Results Table -->
        <section class="accessories-selector__results-table">
            <div class="accessories-selector__table-container">
                <table class="accessories-selector__table" v-if="columns.length > 0">
                    <thead>
                        <tr>
                            <th
                                v-for="col in columns"
                                :key="col"
                                @click="toggleSort(col)"
                                class="accessories-selector__th"
                                :class="{
                                    'accessories-selector__th--sorted': sortColumn === col,
                                    'accessories-selector__th--center': isBooleanColumn(col),
                                }"
                            >
                                {{ getColumnLabel(col) }}
                                <span v-if="sortColumn === col" class="accessories-selector__sort-icon">
                                    {{ sortDirection === 'asc' ? '&#9650;' : '&#9660;' }}
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="item in sortedAccessories"
                            :key="item.id"
                            class="accessories-selector__row"
                        >
                            <td
                                v-for="col in columns"
                                :key="col"
                                :class="{ 'accessories-selector__td--center': typeof item[col] === 'boolean' }"
                            >
                                <template v-if="col === 'title' && item.link">
                                    <a :href="item.link">{{ item[col] }}</a>
                                </template>
                                <template v-else-if="col === 'listPrice' && item[col] != null">
                                    {{ formatPrice(item[col]) }}
                                </template>
                                <template v-else-if="col === 'cableLength' && item[col] != null">
                                    {{ formatLength(item[col]) }}
                                </template>
                                <template v-else-if="typeof item[col] === 'boolean'">
                                    <span
                                        v-if="item[col]"
                                        class="accessories-selector__check"
                                        aria-label="Yes"
                                    >&#10003;</span>
                                </template>
                                <template v-else>
                                    {{ formatValue(item[col]) }}
                                </template>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>

    </div>
</template>

<script>
import vSelect from 'vue-select';

export default {
    name: 'AccessoriesSelector',

    components: {
        vSelect,
    },

    props: {
        apiUrl: {
            type: String,
            required: true,
        },
        category: {
            type: String,
            default: '',
        },
        translations: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            accessories: [],
            columns: [],
            sortKeys: {},
            filters: {},
            // Werte, die der Filter als eine Option zeigt: { key: [{ prefix, members, label? }] }
            filterGroups: {},
            activeFilters: {},
            sortColumn: null,
            sortDirection: 'asc',
            isInitialLoad: true,
        };
    },

    computed: {
        filterKeys() {
            return Object.keys(this.filters);
        },

        filteredAccessories() {
            let filtered = this.accessories;

            for (const key of this.filterKeys) {
                const selected = this.activeFilters[key];
                if (selected && selected.length > 0) {
                    // Mehrwertige Zellen (z.B. Kameraserien) treffen, sobald
                    // eine der gewaehlten Optionen enthalten ist.
                    filtered = filtered.filter(item => this.optionsOf(item, key).some(option => selected.includes(option)));
                }
            }

            return filtered;
        },

        sortedAccessories() {
            const items = [...this.filteredAccessories];

            if (!this.sortColumn) {
                return items;
            }

            const col = this.sortKeys[this.sortColumn] || this.sortColumn;
            const dir = this.sortDirection === 'asc' ? 1 : -1;

            items.sort((a, b) => {
                const aVal = a[col];
                const bVal = b[col];

                if (aVal == null && bVal == null) return 0;
                if (aVal == null) return 1;
                if (bVal == null) return -1;

                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return (aVal - bVal) * dir;
                }

                return String(aVal).localeCompare(String(bVal)) * dir;
            });

            return items;
        },

        availableFilterOptions() {
            const options = {};

            for (const key of this.filterKeys) {
                const values = new Set();
                // Use intermediate filtering (exclude current filter)
                const intermediate = this.getIntermediateFiltered(key);
                intermediate.forEach(item => {
                    this.optionsOf(item, key).forEach(option => values.add(option));
                });
                options[key] = Array.from(values).sort();
            }

            return options;
        },

        // Je Filter: Wert -> Name der Option, unter der er zusammengefasst ist.
        optionNames() {
            const names = {};

            for (const key of Object.keys(this.filterGroups)) {
                names[key] = {};
                for (const group of this.filterGroups[key]) {
                    // Die Kameraspalte liefert den fertigen Namen ("HR Camera"), andere Spalten "<Praefix> - <Spalte>".
                    const label = group.label || `${group.prefix} - ${this.getColumnLabel(key)}`;
                    for (const member of group.members) {
                        names[key][member] = label;
                    }
                }
            }

            return names;
        },

        resultsCountText() {
            const count = this.filteredAccessories.length;
            return this.translations.resultsCount?.replace('{count}', count) || `${count} results`;
        },

        hasActiveFilters() {
            return this.filterKeys.some(key => {
                const selected = this.activeFilters[key];
                return selected && selected.length > 0;
            });
        },
    },

    methods: {
        async loadData() {
            try {
                const urlParams = new URLSearchParams(window.location.search);
                const flush = urlParams.get('flush');

                let apiUrl = this.apiUrl;
                if (flush) {
                    apiUrl += (this.apiUrl.includes('?') ? '&' : '?') + 'flush=' + flush;
                }

                const response = await fetch(apiUrl);
                const data = await response.json();

                this.accessories = data.accessories || [];
                this.columns = data.columns || [];
                this.sortKeys = data.sortKeys || {};
                this.filters = data.filters || {};
                this.filterGroups = data.filterGroups || {};

                // Initialize active filters
                const active = {};
                for (const key of Object.keys(this.filters)) {
                    active[key] = [];
                }
                this.activeFilters = active;

                // Load filters from URL
                this.loadFiltersFromUrl();

                this.$nextTick(() => {
                    this.isInitialLoad = false;
                });
            } catch (error) {
                console.error('Error loading accessories data:', error);
            }
        },

        getColumnLabel(key) {
            return this.translations[key] || key;
        },

        // Optionen des Filters, die ein Produkt trifft (zusammengefasste Werte unter ihrem Gruppennamen).
        optionsOf(item, key) {
            const names = this.optionNames[key] || {};
            const options = new Set();

            for (const entry of [].concat(item[key] ?? [])) {
                if (entry != null && entry !== '') {
                    options.add(names[entry] ?? entry);
                }
            }

            return Array.from(options);
        },

        formatValue(val) {
            if (val == null) return '-';
            if (Array.isArray(val)) return val.join(', ');
            return val;
        },

        // Generisch fuer jede Boolean-Spalte (z.B. Power/I-O/Triggering bei
        // Power IO Connections), nicht an bestimmte Spaltennamen gebunden.
        // Prueft die geladenen Datensaetze, weil 'columns' selbst keine Typinfo
        // traegt.
        isBooleanColumn(col) {
            return this.accessories.some((item) => typeof item[col] === 'boolean');
        },

        formatPrice(val) {
            if (val == null) return '-';
            return new Intl.NumberFormat('de-DE', {
                style: 'currency',
                currency: 'EUR',
            }).format(val);
        },

        formatLength(val) {
            // Struktur-Datei: "Display as: 0.5 m | 1 m | 20 m"
            // parseFloat entfernt nachlaufende Nullen: 3.00 -> 3, 0.50 -> 0.5
            return parseFloat(val) + ' m';
        },

        toggleSort(col) {
            if (this.sortColumn === col) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
            } else {
                this.sortColumn = col;
                this.sortDirection = 'asc';
            }
        },

        getIntermediateFiltered(excludeFilter) {
            let filtered = this.accessories;

            for (const key of this.filterKeys) {
                if (key === excludeFilter) continue;
                const selected = this.activeFilters[key];
                if (selected && selected.length > 0) {
                    filtered = filtered.filter(item => this.optionsOf(item, key).some(option => selected.includes(option)));
                }
            }

            return filtered;
        },

        resetFilters() {
            for (const key of this.filterKeys) {
                this.activeFilters[key] = [];
            }
        },

        loadFiltersFromUrl() {
            const urlParams = new URLSearchParams(window.location.search);

            for (const key of this.filterKeys) {
                if (urlParams.has(key)) {
                    // Ein einzelner Wert, der jetzt in einer Gruppe steckt, waehlt die Gruppe;
                    // unbekannte Werte werden ignoriert statt die Tabelle zu leeren.
                    const names = this.optionNames[key] || {};
                    const known = new Set((this.filters[key] || []).map(value => names[value] ?? value));
                    const values = urlParams.get(key).split(',')
                        .filter(v => v)
                        .map(value => names[value] ?? value)
                        .filter(value => known.has(value));
                    this.activeFilters[key] = Array.from(new Set(values));
                }
            }

            if (urlParams.has('sort')) {
                this.sortColumn = urlParams.get('sort');
            }
            if (urlParams.has('dir')) {
                this.sortDirection = urlParams.get('dir') === 'desc' ? 'desc' : 'asc';
            }
        },

        updateUrl() {
            if (this.isInitialLoad) return;

            const params = new URLSearchParams();

            for (const key of this.filterKeys) {
                const selected = this.activeFilters[key];
                if (selected && selected.length > 0) {
                    params.set(key, selected.join(','));
                }
            }

            if (this.sortColumn) {
                params.set('sort', this.sortColumn);
                if (this.sortDirection !== 'asc') {
                    params.set('dir', this.sortDirection);
                }
            }

            const queryString = params.toString();
            const newUrl = queryString
                ? `${window.location.pathname}?${queryString}`
                : window.location.pathname;

            window.history.pushState({}, '', newUrl);
        },
    },

    watch: {
        activeFilters: {
            handler() {
                this.updateUrl();
            },
            deep: true,
        },
        sortColumn() {
            this.updateUrl();
        },
        sortDirection() {
            this.updateUrl();
        },
    },

    mounted() {
        this.loadData();
    },
};
</script>
